import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { clientNames, projectCategories, projects } from '../data/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { navigateTo, navigateToHomeSection } from '../utils/navigation';
import AutoVideo from './AutoVideo';

const ClientMarquee = ({ clients, reverse = false }) => (
  <div className="client-marquee">
    <div className={`client-marquee-track ${reverse ? 'client-marquee-track-reverse' : ''}`}>
      {[...clients, ...clients].map((client, index) => (
        <span
          key={`${client}-${index}`}
          className="client-marquee-pill"
        >
          {client}
        </span>
      ))}
    </div>
  </div>
);

const ProjectStory = ({ project, index }) => {
  const [storyRef, storyVisible] = useScrollAnimation(0.15);

  return (
    <section
      ref={storyRef}
      className={`grid items-start gap-10 lg:gap-14 ${index % 2 === 0 ? 'lg:grid-cols-[1.1fr_0.9fr]' : 'lg:grid-cols-[0.9fr_1.1fr]'} scroll-fade-in ${storyVisible ? 'visible' : ''}`}
    >
      <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
        <div className="overflow-hidden rounded-[30px] border border-white/10 shadow-[0_24px_80px_rgba(2,6,23,0.55)]">
          {project.video ? (
            <div className="aspect-video">
              <AutoVideo
                src={project.video}
                poster={project.videoPoster || project.image}
                className="h-full w-full"
                videoClassName="h-full w-full object-cover"
              />
            </div>
          ) : (
            <img
              src={project.image}
              alt={project.title}
              className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
            />
          )}
        </div>
      </div>

      <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
        <p className="text-xs uppercase tracking-[0.28em] text-blue-400">Actual Site</p>
        <h2 className="mt-4 text-3xl md:text-4xl font-semibold leading-tight text-white">
          {project.title}
        </h2>
        {project.location ? (
          <p className="mt-3 text-sm uppercase tracking-[0.2em] text-gray-500">
            {project.location}
          </p>
        ) : null}
        <div className="mt-6 h-px w-20 bg-blue-500/60" />
        <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-gray-400">
          {project.description}
        </p>

        <div className="mt-10">
          <p className="text-xs uppercase tracking-[0.28em] text-blue-400">Project Details</p>
          <div className="mt-4 space-y-3 text-sm md:text-base leading-relaxed text-gray-300">
            <p>Environment: {project.title}</p>
            {project.location ? <p>Location: {project.location}</p> : null}
            <p>Category: {project.category}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProjectCategoryPage = ({ slug }) => {
  const category = projectCategories.find((item) => item.slug === slug);
  const categoryProjects = projects.filter((project) => project.categorySlug === slug);
  const [heroRef, heroVisible] = useScrollAnimation(0.15);
  const [clientsRef, clientsVisible] = useScrollAnimation(0.2);
  const selectedClients = clientNames.slice(0, 18);
  const secondaryClients = clientNames.slice(18, 36);
  const pageTheme = {
    corporate: {
      shellGlow: 'radial-gradient(circle at 18% 16%, rgba(37,99,235,0.18), transparent 30%), radial-gradient(circle at 84% 24%, rgba(14,165,233,0.10), transparent 24%)',
      heroOverlay: 'linear-gradient(120deg,rgba(2,6,23,0.95)_0%,rgba(3,7,18,0.76)_42%,rgba(15,23,42,0.34)_100%)',
      gridOpacity: 0.12
    },
    'manufacturing-industry': {
      shellGlow: 'radial-gradient(circle at 14% 18%, rgba(59,130,246,0.14), transparent 28%), radial-gradient(circle at 86% 20%, rgba(148,163,184,0.10), transparent 24%)',
      heroOverlay: 'linear-gradient(120deg,rgba(2,6,23,0.96)_0%,rgba(15,23,42,0.82)_42%,rgba(30,41,59,0.38)_100%)',
      gridOpacity: 0.1
    }
  }[slug] || {
    shellGlow: 'radial-gradient(circle at 18% 16%, rgba(37,99,235,0.12), transparent 28%)',
    heroOverlay: 'linear-gradient(120deg,rgba(2,6,23,0.96)_0%,rgba(2,6,23,0.78)_42%,rgba(2,6,23,0.42)_100%)',
    gridOpacity: 0.15
  };

  if (!category) {
    return (
      <section className="min-h-screen bg-gradient-to-b from-slate-950 via-black to-black pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm uppercase tracking-[0.24em] text-blue-400">Projects</p>
          <h1 className="mt-4 text-4xl md:text-5xl font-semibold text-white">Category Not Found</h1>
          <p className="mt-6 text-gray-400">
            The requested project category does not exist. Return to the homepage to explore available categories.
          </p>
          <button
            type="button"
            onClick={() => navigateTo('/')}
            className="mt-10 inline-flex items-center gap-2 rounded-md border border-blue-500/30 px-6 py-3 text-blue-300 transition-colors duration-300 hover:bg-blue-500/10"
          >
            <ArrowLeft size={16} />
            Back to homepage
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen bg-gradient-to-b from-slate-950 via-black to-slate-950 pt-24 pb-24 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: pageTheme.shellGlow }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-20">
        <div
          ref={heroRef}
          className={`relative overflow-hidden rounded-[36px] border border-white/10 shadow-[0_28px_90px_rgba(2,6,23,0.34)] scroll-fade-in ${heroVisible ? 'visible' : ''}`}
        >
          <div className="absolute inset-0">
            {category.heroEmbed ? (
              <iframe
                src={category.heroEmbed}
                title={`${category.title} hero video`}
                className="h-full w-full scale-[1.18] object-cover pointer-events-none"
                allow="autoplay; encrypted-media; picture-in-picture"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : category.heroVideo ? (
              <AutoVideo
                src={category.heroVideo}
                poster={category.heroPoster || category.image}
                className="h-full w-full"
                videoClassName="h-full w-full object-cover scale-[1.02]"
              />
            ) : (
              <img
                src={category.image}
                alt={category.title}
                className="h-full w-full object-cover"
              />
            )}
            <div className="absolute inset-0" style={{ background: pageTheme.heroOverlay }} />
            <div className="absolute inset-0" style={{
              opacity: pageTheme.gridOpacity,
              backgroundImage: 'linear-gradient(rgba(59,130,246,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.35) 1px, transparent 1px)',
              backgroundSize: '64px 64px'
            }} />
          </div>

          <div className="relative grid min-h-[520px] items-end px-6 py-10 md:px-10 md:py-12 lg:min-h-[620px]">
            <div className="max-w-4xl">
              <button
                type="button"
                onClick={() => navigateToHomeSection('projects')}
                className="inline-flex items-center gap-2 text-sm text-gray-200 transition-colors duration-300 hover:text-blue-300"
              >
                <ArrowLeft size={16} />
                Back to project categories
              </button>

              <p className="mt-12 text-xs uppercase tracking-[0.32em] text-blue-300">{category.eyebrow}</p>
              <h1 className="project-hero-title mt-5 text-4xl md:text-6xl font-semibold leading-[1.05] text-white max-w-4xl">
                {category.title} Projects
              </h1>
              <p className="mt-6 max-w-2xl text-base md:text-xl leading-relaxed text-gray-300">
                {category.summary}
              </p>

              <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/15 bg-black/30 px-5 py-3 text-sm text-gray-200 backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
                This page explains the category, shows actual completed sites, and gives a clearer view of where it fits.
              </div>
            </div>
          </div>
        </div>

        <section className="mt-16 grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-[30px] border border-white/10 bg-white/[0.03] p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.28em] text-blue-400">What This Category Covers</p>
            <p className="mt-5 max-w-3xl text-base md:text-lg leading-relaxed text-gray-300">
              {category.overview}
            </p>
          </div>
          <div className="rounded-[30px] border border-white/10 bg-white/[0.03] p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.28em] text-blue-400">Typical Scope</p>
            <div className="mt-5 space-y-3">
              {category.capabilities?.map((item) => (
                <div key={item} className="text-gray-300 text-sm md:text-base leading-relaxed">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-20">
          <div className="mb-10">
            <p className="text-xs uppercase tracking-[0.28em] text-blue-400">Actual Sites</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-white">
              Completed Sites In This Category
            </h2>
            <p className="mt-4 max-w-3xl text-gray-400 leading-relaxed">
              These are actual project sites completed within {category.title.toLowerCase()}, shown with visuals and site names so visitors can clearly understand the real nature of the work.
            </p>
          </div>

          <div className="space-y-20">
          {categoryProjects.map((project, index) => (
            <ProjectStory key={project.id} project={project} index={index} />
          ))}
          </div>
        </div>

        <section
          ref={clientsRef}
          className={`mt-24 scroll-fade-in ${clientsVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-blue-400">Selected Clients</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-white">
              Trusted Across Builders, Institutions, and Enterprises
            </h2>
            <p className="mt-4 max-w-3xl mx-auto text-gray-400 leading-relaxed">
              A live mix of developers, hospitals, infrastructure groups, industrial brands, and commercial operators from our client network.
            </p>
          </div>

          <ClientMarquee clients={selectedClients} />
          <div className="mt-5">
            <ClientMarquee clients={secondaryClients} reverse />
          </div>
        </section>

        <div className="mt-20 flex justify-center">
          <button
            type="button"
            onClick={() => navigateToHomeSection('contact')}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-6 py-3 text-white shadow-lg shadow-blue-950/30 transition-colors duration-300 hover:bg-blue-700"
          >
            Discuss a Similar Project
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProjectCategoryPage;
